import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Switch, Slider, Select, Tooltip, Collapse, Radio, Tag, Divider, notification } from 'antd';
import {
    BsCamera, BsMic, BsStars, BsShieldCheck,
    BsSpeedometer, BsImage, BsSliders, BsPalette
} from 'react-icons/bs';
import {
    MdBlurOn, MdAutoFixHigh, MdFaceRetouchingNatural,
    MdGraphicEq, MdCenterFocusWeak, MdWallpaper
} from 'react-icons/md';
import { HiSparkles } from 'react-icons/hi2';
import { IoColorPaletteOutline } from 'react-icons/io5';
import { RiVoiceprintFill } from 'react-icons/ri';
import './MediaEnhancementsPanel.css';

const { Panel } = Collapse;
const { Option } = Select;

/**
 * MediaEnhancementsPanel
 * 
 * UI panel for toggling video and audio enhancements.
 * Appears as a tab in the moderator's MediaPannel or as
 * a standalone panel for participants (when allowed).
 * 
 * Props:
 *  - mediaPipeline: MediaPipeline instance
 *  - isModerator: boolean
 *  - enhancementsAllowed: boolean (for participants, controlled by moderator)
 *  - onPermissionChange: (allowed: boolean) => void (moderator only)
 *  - onPerformanceModeChange: (mode: string) => void (moderator only)
 *  - performanceMode: string
 */
const MediaEnhancementsPanel = ({
    mediaPipeline,
    isModerator = false,
    enhancementsAllowed = true,
    onPermissionChange,
    onPerformanceModeChange,
    performanceMode = 'balanced',
    compact = false
}) => {
    // Video effects state
    const [videoEffects, setVideoEffects] = useState({
        backgroundBlur: false,
        backgroundBlurStrength: 10,
        transparentBackground: false,
        virtualBackground: false,
        virtualBackgroundType: 'color',
        virtualBackgroundValue: '#0A84FF',
        noiseReduction: false,
        noiseReductionStrength: 0.5,
        autoLighting: false,
        autoLightingStrength: 0.5,
        faceSmoothing: false,
        faceSmoothingStrength: 0.4,
        colorGrading: 'none',
        creativeEffect: 'none',
        autoFraming: false,
        faceStabilization: false,
        dynamicBgBlur: false,
    });

    // Audio effects state
    const [audioEffects, setAudioEffects] = useState({
        noiseCancellation: false,
        echoCancellation: false,
        voiceClarity: false,
        autoGainControl: false,
        backgroundNoiseSuppression: false,
    });

    // Sync state from pipeline on mount
    useEffect(() => {
        if (mediaPipeline) {
            setVideoEffects(mediaPipeline.getVideoEffects());
            setAudioEffects(mediaPipeline.getAudioEffects());
        }
    }, [mediaPipeline]);

    // Apply video effects to pipeline
    const updateVideoEffect = useCallback((key, value) => {
        if (!mediaPipeline) return;

        const newEffects = { ...videoEffects, [key]: value };

        // Mutual exclusivity for background effects
        if (key === 'backgroundBlur' && value) {
            newEffects.transparentBackground = false;
            newEffects.virtualBackground = false;
            newEffects.dynamicBgBlur = false;
        } else if (key === 'transparentBackground' && value) {
            newEffects.backgroundBlur = false;
            newEffects.virtualBackground = false;
            newEffects.dynamicBgBlur = false;
        } else if (key === 'virtualBackground' && value) {
            newEffects.backgroundBlur = false;
            newEffects.transparentBackground = false;
            newEffects.dynamicBgBlur = false;
        } else if (key === 'dynamicBgBlur' && value) {
            newEffects.backgroundBlur = false;
            newEffects.transparentBackground = false;
            newEffects.virtualBackground = false;
        }

        setVideoEffects(newEffects);
        mediaPipeline.setVideoEffects(newEffects);
    }, [mediaPipeline, videoEffects]);

    // Apply audio effects to pipeline
    const updateAudioEffect = useCallback((key, value) => {
        if (!mediaPipeline) return;

        const newEffects = { ...audioEffects, [key]: value };
        setAudioEffects(newEffects);
        mediaPipeline.setAudioEffects(newEffects);
    }, [mediaPipeline, audioEffects]);

    // Performance mode handler
    const handlePerformanceModeChange = useCallback((mode) => {
        if (mediaPipeline) {
            mediaPipeline.setPerformanceMode(mode);
        }
        if (onPerformanceModeChange) {
            onPerformanceModeChange(mode);
        }
    }, [mediaPipeline, onPerformanceModeChange]);

    // Virtual background presets
    const bgColorPresets = ['#0A84FF', '#30D158', '#FF453A', '#FF9F0A', '#BF5AF2', '#1C1C1E', '#F2F2F7'];

    const colorGradingOptions = [
        { value: 'none', label: 'None' },
        { value: 'warm', label: '🌅 Warm' },
        { value: 'cool', label: '❄️ Cool' },
        { value: 'vintage', label: '📷 Vintage' },
        { value: 'cinematic', label: '🎬 Cinematic' },
        { value: 'bright', label: '☀️ Bright' },
        { value: 'bw', label: '⬛ B&W' },
        { value: 'dramatic', label: '🎭 Dramatic' },
    ];

    const creativeEffectOptions = [
        { value: 'none', label: 'None' },
        { value: 'vignette', label: '🔲 Vignette' },
        { value: 'grain', label: '📻 Film Grain' },
        { value: 'glow', label: '✨ Soft Glow' },
    ];

    return (
        <div className={compact ? "media-enhancements-panel compact" : "media-enhancements-panel"}>
            {/* Header */}
            
          {isModerator && (
                <div className="permission-control">
                    <div className="permission-row">
                        <BsShieldCheck size={14} />
                        <span>Allow participants to use enhancements</span>
                        <Switch
                            size="small"
                            checked={enhancementsAllowed}
                            onChange={(checked) => onPermissionChange && onPermissionChange(checked)}
                        />
                    </div>
                </div>
            )}
            <Collapse
                bordered={false}
                defaultActiveKey={compact ? [] : ['video-bg']}
                className="enhancements-collapse"
                expandIconPosition="right"
            >
                {/* ===== VIDEO: Background ===== */}
                <Panel
                    header={
                        <span className="panel-header">
                            <MdBlurOn size={16} />
                            Background
                        </span>
                    }
                    key="video-bg"
                >
                    <div className="effect-group">
                        <div className="effect-row">
                            <span>Background Blur</span>
                            <Switch
                                size="small"
                                checked={videoEffects.backgroundBlur}
                                onChange={(val) => updateVideoEffect('backgroundBlur', val)}
                            />
                        </div>
                        {videoEffects.backgroundBlur && (
                            <div className="effect-sub">
                                <span className="sub-label">Blur Strength</span>
                                <Slider
                                    min={1}
                                    max={40}
                                    value={videoEffects.backgroundBlurStrength}
                                    onChange={(val) => updateVideoEffect('backgroundBlurStrength', val)}
                                />
                            </div>
                        )}

                        <div className="effect-row">
                            <span>Transparent Background</span>
                            <Switch
                                size="small"
                                checked={videoEffects.transparentBackground}
                                onChange={(val) => updateVideoEffect('transparentBackground', val)}
                            />
                        </div>

                        <div className="effect-row">
                            <span>Virtual Background</span>
                            <Switch
                                size="small"
                                checked={videoEffects.virtualBackground}
                                onChange={(val) => updateVideoEffect('virtualBackground', val)}
                            />
                        </div>
                        {videoEffects.virtualBackground && (
                            <div className="effect-sub">
                                <Radio.Group
                                    value={videoEffects.virtualBackgroundType}
                                    onChange={(e) => updateVideoEffect('virtualBackgroundType', e.target.value)}
                                    size="small"
                                    className="bg-type-select"
                                >
                                    <Radio.Button value="color">Color</Radio.Button>
                                    <Radio.Button value="image">Image</Radio.Button>
                                </Radio.Group>

                                {videoEffects.virtualBackgroundType === 'color' && (
                                    <div className="color-presets">
                                        {bgColorPresets.map(color => (
                                            <button
                                                key={color}
                                                className={`color-swatch ${videoEffects.virtualBackgroundValue === color ? 'active' : ''}`}
                                                style={{ backgroundColor: color }}
                                                onClick={() => updateVideoEffect('virtualBackgroundValue', color)}
                                            />
                                        ))}
                                    </div>
                                )}

                                {videoEffects.virtualBackgroundType === 'image' && (
                                    <div className="image-url-input">
                                        <input
                                            type="text"
                                            placeholder="Enter image URL..."
                                            value={videoEffects.virtualBackgroundValue || ''}
                                            onChange={(e) => updateVideoEffect('virtualBackgroundValue', e.target.value)}
                                            className="bg-image-input"
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="effect-row">
                            <span>Dynamic Motion Blur</span>
                            <Switch
                                size="small"
                                checked={videoEffects.dynamicBgBlur}
                                onChange={(val) => updateVideoEffect('dynamicBgBlur', val)}
                            />
                        </div>
                    </div>
                </Panel>

                {/* ===== VIDEO: Quality ===== */}
                <Panel
                    header={
                        <span className="panel-header">
                            <MdAutoFixHigh size={16} />
                            Video Quality
                        </span>
                    }
                    key="video-quality"
                >
                    <div className="effect-group">
                        <div className="effect-row">
                            <span>Noise Reduction</span>
                            <Switch
                                size="small"
                                checked={videoEffects.noiseReduction}
                                onChange={(val) => updateVideoEffect('noiseReduction', val)}
                            />
                        </div>
                        {videoEffects.noiseReduction && (
                            <div className="effect-sub">
                                <span className="sub-label">Strength</span>
                                <Slider
                                    min={0}
                                    max={100}
                                    value={videoEffects.noiseReductionStrength * 100}
                                    onChange={(val) => updateVideoEffect('noiseReductionStrength', val / 100)}
                                />
                            </div>
                        )}

                        <div className="effect-row">
                            <span>Auto Lighting</span>
                            <Switch
                                size="small"
                                checked={videoEffects.autoLighting}
                                onChange={(val) => updateVideoEffect('autoLighting', val)}
                            />
                        </div>
                        {videoEffects.autoLighting && (
                            <div className="effect-sub">
                                <span className="sub-label">Strength</span>
                                <Slider
                                    min={0}
                                    max={100}
                                    value={videoEffects.autoLightingStrength * 100}
                                    onChange={(val) => updateVideoEffect('autoLightingStrength', val / 100)}
                                />
                            </div>
                        )}

                        <div className="effect-row">
                            <span>Face Smoothing</span>
                            <Switch
                                size="small"
                                checked={videoEffects.faceSmoothing}
                                onChange={(val) => updateVideoEffect('faceSmoothing', val)}
                            />
                        </div>
                        {videoEffects.faceSmoothing && (
                            <div className="effect-sub">
                                <span className="sub-label">Intensity</span>
                                <Slider
                                    min={0}
                                    max={100}
                                    value={videoEffects.faceSmoothingStrength * 100}
                                    onChange={(val) => updateVideoEffect('faceSmoothingStrength', val / 100)}
                                />
                            </div>
                        )}
                    </div>
                </Panel>

                {/* ===== VIDEO: Effects ===== */}
                <Panel
                    header={
                        <span className="panel-header">
                            <IoColorPaletteOutline size={16} />
                            Effects & Filters
                        </span>
                    }
                    key="video-effects"
                >
                    <div className="effect-group">
                        <div className="effect-row-vertical">
                            <span>Color Grading</span>
                            <Select
                                value={videoEffects.colorGrading}
                                onChange={(val) => updateVideoEffect('colorGrading', val)}
                                size="small"
                                className="grading-select"
                                style={{ width: '100%' }}
                            >
                                {colorGradingOptions.map(opt => (
                                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                                ))}
                            </Select>
                        </div>

                        <div className="effect-row-vertical">
                            <span>Creative Effect</span>
                            <Select
                                value={videoEffects.creativeEffect}
                                onChange={(val) => updateVideoEffect('creativeEffect', val)}
                                size="small"
                                className="effect-select"
                                style={{ width: '100%' }}
                            >
                                {creativeEffectOptions.map(opt => (
                                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                                ))}
                            </Select>
                        </div>
                    </div>
                </Panel>

                {/* ===== VIDEO: Smart Features ===== */}
                <Panel
                    header={
                        <span className="panel-header">
                            <MdCenterFocusWeak size={16} />
                            Smart Features
                        </span>
                    }
                    key="video-smart"
                >
                    <div className="effect-group">
                        <div className="effect-row">
                            <span>AI Auto Framing</span>
                            <Switch
                                size="small"
                                checked={videoEffects.autoFraming}
                                onChange={(val) => updateVideoEffect('autoFraming', val)}
                            />
                        </div>
                        <div className="effect-row">
                            <span>Face Stabilization</span>
                            <Switch
                                size="small"
                                checked={videoEffects.faceStabilization}
                                onChange={(val) => updateVideoEffect('faceStabilization', val)}
                            />
                        </div>
                    </div>
                </Panel>

                {/* ===== AUDIO ===== */}
                <Panel
                    header={
                        <span className="panel-header">
                            <MdGraphicEq size={16} />
                            Audio Enhancements
                        </span>
                    }
                    key="audio"
                >
                    <div className="effect-group">
                        <div className="effect-row">
                            <span>Noise Cancellation</span>
                            <Switch
                                size="small"
                                checked={audioEffects.noiseCancellation}
                                onChange={(val) => updateAudioEffect('noiseCancellation', val)}
                            />
                        </div>
                        <div className="effect-row">
                            <span>Echo Cancellation</span>
                            <Switch
                                size="small"
                                checked={audioEffects.echoCancellation}
                                onChange={(val) => updateAudioEffect('echoCancellation', val)}
                            />
                        </div>
                        <div className="effect-row">
                            <span>Voice Clarity</span>
                            <Switch
                                size="small"
                                checked={audioEffects.voiceClarity}
                                onChange={(val) => updateAudioEffect('voiceClarity', val)}
                            />
                        </div>
                        <div className="effect-row">
                            <span>Auto Gain Control</span>
                            <Switch
                                size="small"
                                checked={audioEffects.autoGainControl}
                                onChange={(val) => updateAudioEffect('autoGainControl', val)}
                            />
                        </div>
                        <div className="effect-row">
                            <span>Background Noise Suppression</span>
                            <Switch
                                size="small"
                                checked={audioEffects.backgroundNoiseSuppression}
                                onChange={(val) => updateAudioEffect('backgroundNoiseSuppression', val)}
                            />
                        </div>
                    </div>
                </Panel>

                {/* ===== Performance (Moderator only) ===== */}
                {isModerator && (
                    <Panel
                        header={
                            <span className="panel-header">
                                <BsSpeedometer size={16} />
                                Performance
                            </span>
                        }
                        key="performance"
                    >
                        <div className="effect-group">
                            <div className="performance-modes">
                                <Radio.Group
                                    value={performanceMode}
                                    onChange={(e) => handlePerformanceModeChange(e.target.value)}
                                    className="performance-radio-group"
                                >
                                    <Radio.Button value="highquality">
                                        <div className="perf-option">
                                            <strong>High Quality</strong>
                                            <small>Best visuals, higher CPU</small>
                                        </div>
                                    </Radio.Button>
                                    <Radio.Button value="balanced">
                                        <div className="perf-option">
                                            <strong>Balanced</strong>
                                            <small>Good quality & performance</small>
                                        </div>
                                    </Radio.Button>
                                    <Radio.Button value="performance">
                                        <div className="perf-option">
                                            <strong>Performance</strong>
                                            <small>Smooth on any device</small>
                                        </div>
                                    </Radio.Button>
                                </Radio.Group>
                            </div>
                        </div>
                    </Panel>
                )}
            </Collapse>
        </div>
    );
};

export default MediaEnhancementsPanel;
